import PropTypes from "prop-types";
import { HiOutlineCheck } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES, PricingPlan } from "../../../data-access";
import { FormButton } from "../../../ui";
import { PlanSkeleton } from "./plan-skeleton";
import { ByokKeyButton } from "../../byok-key";
import "./plan.scss";
import { BillingInterval } from "../plans";

interface IPlanProps {
  id: number;
  title: PricingPlan;
  price: number;
  features: string[];
  periodName: string;
  isCurrent?: boolean;
  isProcessing?: boolean;
  priceWithDiscount?: string;
  yearlyPrice?: number;
  hasYearly?: boolean;
  billingInterval?: BillingInterval;
  onSelect: (planId: number, interval: BillingInterval) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  isRecommended?: boolean;
  hasActiveSubscription?: boolean;
  currentPlanPrice?: number;
  currentInterval?: BillingInterval;
  isPending?: boolean;
  isCanceling?: boolean;
  cancelAt?: string | null;
  /** Legacy subscriber looking at Flex: the switch is an upgrade even if Flex is cheaper. */
  isFlexMigration?: boolean;
}

const baseClassName = "plan";

export const Plan = ({
  priceWithDiscount,
  isProcessing = false,
  periodName,
  id,
  title,
  price,
  features,
  isCurrent = false,
  isLoading = false,
  isRecommended = false,
  yearlyPrice,
  hasYearly = false,
  billingInterval = "monthly",
  onSelect,
  onCancel,
  hasActiveSubscription = false,
  currentPlanPrice = 0,
  currentInterval = "monthly",
  isPending = false,
  isCanceling = false,
  cancelAt = null,
  isFlexMigration = false,
}: IPlanProps): JSX.Element => {
  const isYearly = billingInterval === "yearly" && hasYearly;
  const displayPrice = isYearly && yearlyPrice ? yearlyPrice : (priceWithDiscount ? +priceWithDiscount : price);
  const displayPeriod = isYearly ? "/year" : `/${periodName}`;
  // On yearly billing the AI-credit allowance is the annual total (12× monthly).
  // The bullet stored in `description` is the monthly phrasing, so rewrite it
  // when displaying the yearly card.
  const displayFeatures = isYearly
    ? features.map((f) =>
        f.replace(
          /^(\d+)\s+AI Credits\s+\/\s+seat\s+\/\s+month$/i,
          (_, n) => `${+n * 12} AI Credits / seat / year`,
        ),
      )
    : features;
  const navigate = useNavigate();

  const isIntervalSwitch = hasActiveSubscription && billingInterval !== currentInterval && !isFlexMigration;
  const isUpgrade = isFlexMigration || price > currentPlanPrice;
  const isDowngrade = !isFlexMigration && price < currentPlanPrice;

  return (
    <PlanSkeleton isLoading={isLoading || isProcessing}>
      <div className={`${baseClassName}${isCurrent ? ` ${baseClassName}--current` : ""}${isRecommended ? ` ${baseClassName}--recommended` : ""}`}>
        {isCurrent && isCanceling ? (
          <span className={`${baseClassName}__canceling-badge`}>Canceling</span>
        ) : isCurrent ? (
          <span className={`${baseClassName}__current-badge`}>Current Plan</span>
        ) : isPending && !isCurrent ? (
          <span className={`${baseClassName}__pending-badge`}>Scheduled</span>
        ) : isRecommended && !isCurrent && !isPending ? (
          <span className={`${baseClassName}__recommended-badge`}>Recommended</span>
        ) : null}

        <div className={`${baseClassName}__header`}>
          <h2 className={`${baseClassName}__title`}>
            {title}
          </h2>
          <div className={`${baseClassName}__price`}>
            <span className={`${baseClassName}__price-currency`}>$</span>
            {!isYearly && priceWithDiscount && (
              <span className={`${baseClassName}__price-old`}>{price}</span>
            )}
            <span className={`${baseClassName}__price-value`}>{displayPrice}</span>
            <span className={`${baseClassName}__price-period`}>{displayPeriod}</span>
          </div>
        </div>

        <ul className={`${baseClassName}__features`}>
          {(Array.isArray(displayFeatures) ? displayFeatures : []).map((feature: string, index: number) => (
            <li key={`feature-${index}`} className={`${baseClassName}__feature`}>
              <span className={`${baseClassName}__feature-icon`}>
                <HiOutlineCheck size={14} />
              </span>
              {feature}
            </li>
          ))}
        </ul>

        <div className={`${baseClassName}__footer`}>
          {isCurrent && isCanceling ? (
            <>
              <div className={`${baseClassName}__footer-row`}>
                <FormButton
                  className={`${baseClassName}__button`}
                  theme="green-light"
                  onClick={() => navigate(APP_ROUTES.team)}
                >
                  Manage Team
                </FormButton>
                <ByokKeyButton />
              </div>
              <p className={`${baseClassName}__cancel-info`}>
                {cancelAt
                  ? `Cancels on ${new Date(cancelAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                  : "Cancels at end of billing period"}
              </p>
            </>
          ) : isCurrent && hasActiveSubscription ? (
            <>
              <div className={`${baseClassName}__footer-row`}>
                <FormButton
                  className={`${baseClassName}__button`}
                  theme="green-light"
                  loading={isProcessing}
                  disabled={isProcessing}
                  onClick={() => navigate(APP_ROUTES.team)}
                >
                  Manage Team
                </FormButton>
                <ByokKeyButton />
              </div>
              {onCancel && (
                <button
                  className={`${baseClassName}__cancel`}
                  onClick={onCancel}
                >
                  Cancel Plan
                </button>
              )}
            </>
          ) : isPending ? (
            <FormButton
              className={`${baseClassName}__button`}
              theme="grey"
              disabled
            >
              Scheduled
            </FormButton>
          ) : (
            <FormButton
              className={`${baseClassName}__button`}
              theme={hasActiveSubscription && isDowngrade && !isIntervalSwitch ? "grey" : "green-light"}
              loading={isProcessing}
              disabled={isProcessing}
              onClick={() => onSelect(id, isYearly ? "yearly" : "monthly")}
            >
              {!hasActiveSubscription
                ? "Subscribe"
                : isIntervalSwitch
                  ? "Switch"
                  : isUpgrade
                    ? "Upgrade"
                    : "Downgrade"}
            </FormButton>
          )}
        </div>
      </div>
    </PlanSkeleton>
  );
};

Plan.propTypes = {
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  priceWithDiscount: PropTypes.string,
  periodName: PropTypes.string,
  features: PropTypes.arrayOf(PropTypes.string).isRequired,
  isCurrent: PropTypes.bool,
  isProcessing: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};
